import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  Body2,
  StepActionButton,
  Label,
  Select,
  SelectTrigger,
  Textarea,
  useWizardContext,
} from "@activecollab/components";
import { CREATE_CANCELLATION_REQUEST_SUCCESS } from "~/store/action_types";
import { createCancellationRequest } from "~/store/actions/actions.cancelSubscription";

const firstQuestionAnswers = [
  { id: 0, name: "We don’t need it anymore" },
  { id: 1, name: "Important features are missing" },
  { id: 2, name: "It’s hard to use" },
  { id: 3, name: "We are cutting costs" },
  { id: 4, name: "We found a better tool for us" },
  { id: 5, name: "Other" },
];

const secondQuestionAnswers = [
  { id: 0, name: "Definitely!" },
  { id: 1, name: "Maybe" },
  { id: 2, name: "I don't think so" },
];

const CancelSurvey = () => {
  const { goNext } = useWizardContext();
  const cancelRequest = useSelector((state) => state.cancellationRequestObject);
  const [firstQuestion, setFirstQuestion] = useState(cancelRequest?.survey?.firstQuestion);
  const onFirstQuestionChange = useCallback((id) => {
    if (id === null) return;
    setFirstQuestion(id);
  }, []);
  const [secondQuestion, setSecondQuestion] = useState(cancelRequest?.survey?.secondQuestion);
  const onSecondQuestionChange = useCallback((id) => {
    if (id === null) return;
    setSecondQuestion(id);
  }, []);
  const [feedback, setFeedback] = useState(cancelRequest?.feedback);
  const onFeedbackChange = useCallback((e) => {
    setFeedback(e.target?.value);
  }, []);
  const dispatch = useDispatch();
  const submitSurvey = useCallback(() => {
    dispatch(
      createCancellationRequest(cancelRequest, feedback, {
        firstQuestion,
        secondQuestion,
      })
    ).then((response) => {
      if (response.type === CREATE_CANCELLATION_REQUEST_SUCCESS) {
        goNext();
      }
    });
  }, [cancelRequest, dispatch, feedback, firstQuestion, goNext, secondQuestion]);

  return (
    <>
      <Dialog.Content
        style={{
          maxHeight: "calc(100vh - 305px)",
        }}
      >
        <Body2>
          We`re sorry to see you go. Please let us know why you`re leaving, so we can improve our service. Every bit of
          feedback helps!
        </Body2>
        <div className="tw-flex tw-flex-col tw-mt-6">
          <Label className="tw-mb-1">What’s the main reason for quitting ActiveCollab?</Label>
          <Select
            disableSearch
            forceCloseMenu
            options={firstQuestionAnswers}
            selected={firstQuestion}
            target={
              <SelectTrigger className="tw-max-w-full">
                <div className="tw-flex tw-items-center">
                  {firstQuestion === undefined
                    ? "Select answer..."
                    : firstQuestionAnswers.find((q) => q.id === firstQuestion)?.name}
                </div>
              </SelectTrigger>
            }
            onChange={onFirstQuestionChange}
            disabledInternalSort
            keepSameOptionsOrder
          />
        </div>
        <div className="tw-flex tw-flex-col tw-mt-6">
          <Label className="tw-mb-1">Would you consider giving ActiveCollab another go?</Label>
          <Select
            disableSearch
            forceCloseMenu
            options={secondQuestionAnswers}
            selected={secondQuestion}
            target={
              <SelectTrigger className="tw-max-w-full">
                <div className="tw-flex tw-items-center">
                  {secondQuestion === undefined
                    ? "Select answer..."
                    : secondQuestionAnswers.find((q) => q.id === secondQuestion)?.name}
                </div>
              </SelectTrigger>
            }
            onChange={onSecondQuestionChange}
            disabledInternalSort
            keepSameOptionsOrder
          />
        </div>
        <div className="tw-flex tw-flex-col tw-mt-6">
          <Label className="tw-mb-1">Some additional thoughts?</Label>
          <Textarea
            className="tw-w-full"
            onChange={onFeedbackChange}
            value={feedback}
          />
        </div>
      </Dialog.Content>
      <Dialog.ContentDivider />
      <Dialog.Actions>
        <StepActionButton
          actionLabel="Continue"
          disabled={firstQuestion === undefined || secondQuestion === undefined}
          onNextButtonClick={submitSurvey}
        />
      </Dialog.Actions>
    </>
  );
};

export default CancelSurvey;
